/**
 * Composant de gestion de la whitelist IP
 *
 * ISO 27001 A.9.4.2 - Procédures de connexion sécurisée (contrôle d'accès réseau)
 * Permet aux admins de configurer les IP autorisées pour le tenant
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ipWhitelistApi, IpWhitelistEntry, IpWhitelistConfig, IpWhitelistMode } from '../api/ip-whitelist.api';
import { logger } from '@/utils/logger';
import { confirm } from '@/stores/confirmStore';
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  Plus,
  Trash2,
  Edit2,
  Globe,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Settings,
  Info,
} from 'lucide-react';

// MODE_OPTIONS moved inside IpWhitelistSettings component for i18n support

// Formater une date
const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Vérifier si une entrée est expirée
const isExpired = (expiresAt?: string): boolean => {
  if (!expiresAt) return false;
  return new Date(expiresAt) < new Date();
};

interface IpEntryCardProps {
  entry: IpWhitelistEntry;
  onEdit: (entry: IpWhitelistEntry) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string, isActive: boolean) => void;
  loading: boolean;
}

const IpEntryCard: React.FC<IpEntryCardProps> = ({
  entry,
  onEdit,
  onDelete,
  onToggleActive,
  loading,
}) => {
  const { t } = useTranslation();
  const expired = isExpired(entry.expiresAt);
  const active = entry.isActive && !expired;

  return (
    <div
      className={`
        bg-white/40 border rounded-2xl p-4 transition-all duration-300
        ${active ? 'border-teal-200 bg-teal-50/30' : 'border-gray-200 bg-gray-50/30'}
        ${expired ? 'opacity-60' : ''}
      `}
    >
      <div className="flex items-start justify-between gap-4">
        {/* Info principale */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`
            p-2.5 rounded-xl flex-shrink-0
            ${active ? 'bg-teal-100' : 'bg-gray-100'}
          `}>
            <Globe className={`w-5 h-5 ${active ? 'text-teal-600' : 'text-gray-400'}`} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-gray-900 truncate">{entry.label}</h4>
              {expired && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
                  {t('auth.ipWhitelist.expired')}
                </span>
              )}
              {!entry.isActive && !expired && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                  {t('auth.ipWhitelist.disabled')}
                </span>
              )}
            </div>
            <p className="text-sm font-mono text-gray-600 truncate">{entry.ipAddressOrRange}</p>
            {entry.description && (
              <p className="text-sm text-gray-500 mt-1 line-clamp-1">{entry.description}</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => onToggleActive(entry.id, !entry.isActive)}
            disabled={loading || expired}
            className={`
              p-2 rounded-xl transition-all duration-200
              ${entry.isActive
                ? 'text-teal-600 hover:bg-teal-100'
                : 'text-gray-400 hover:bg-gray-100'
              }
              disabled:opacity-50 disabled:cursor-not-allowed
            `}
            title={entry.isActive ? t('auth.ipWhitelist.deactivate') : t('auth.ipWhitelist.activate')}
          >
            {entry.isActive ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
          </button>

          <button
            onClick={() => onEdit(entry)}
            disabled={loading}
            className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 transition-all duration-200 disabled:opacity-50"
            title={t('auth.ipWhitelist.edit')}
          >
            <Edit2 className="w-5 h-5" />
          </button>

          <button
            onClick={() => onDelete(entry.id)}
            disabled={loading}
            className="p-2 rounded-xl text-red-500 hover:bg-red-50 transition-all duration-200 disabled:opacity-50"
            title={t('auth.ipWhitelist.deleteEntry')}
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Footer avec dates */}
      {entry.expiresAt && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            <span>{t('auth.ipWhitelist.expires')} {formatDate(entry.expiresAt)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

interface AddEditModalProps {
  entry?: IpWhitelistEntry;
  onClose: () => void;
  onSave: (data: { ipAddressOrRange: string; label: string; description?: string; expiresAt?: string }) => void;
  loading: boolean;
}

const AddEditModal: React.FC<AddEditModalProps> = ({ entry, onClose, onSave, loading }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    ipAddressOrRange: entry?.ipAddressOrRange || '',
    label: entry?.label || '',
    description: entry?.description || '',
    expiresAt: entry?.expiresAt ? entry.expiresAt.slice(0, 16) : '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ipAddressOrRange: form.ipAddressOrRange,
      label: form.label,
      description: form.description || undefined,
      expiresAt: form.expiresAt || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white/90 backdrop-blur-xl rounded-3xl border border-white/40 shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-teal-100 rounded-xl">
              <Globe className="w-5 h-5 text-teal-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              {entry ? t('auth.ipWhitelist.editEntry') : t('auth.ipWhitelist.addIp')}
            </h2>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('auth.ipWhitelist.ipAddressOrCidr')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.ipAddressOrRange}
              onChange={(e) => setForm({ ...form, ipAddressOrRange: e.target.value })}
              placeholder="192.168.1.1 ou 192.168.0.0/24"
              disabled={!!entry} // Ne pas modifier l'IP existante
              className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-teal-500/50 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              required
            />
            {!entry && (
              <p className="text-xs text-gray-500 mt-1">
                {t('auth.ipWhitelist.ipExample')}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('auth.ipWhitelist.label')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder={t('auth.ipWhitelist.labelPlaceholder')}
              className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-teal-500/50 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('auth.ipWhitelist.descriptionLabel')}
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder={t('auth.ipWhitelist.descriptionPlaceholder')}
              rows={2}
              className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-teal-500/50 focus:border-transparent resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {t('auth.ipWhitelist.expiresAtLabel')}
            </label>
            <input
              type="datetime-local"
              value={form.expiresAt}
              onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
              className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-900 focus:ring-2 focus:ring-teal-500/50 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('auth.ipWhitelist.expiresAtHint')}
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-5 py-2.5 text-gray-700 hover:bg-gray-100 rounded-xl transition-all duration-200 font-medium disabled:opacity-50"
            >
              {t('auth.ipWhitelist.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || !form.ipAddressOrRange || !form.label}
              className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && <RefreshCw className="w-4 h-4 animate-spin" />}
              {entry ? t('auth.ipWhitelist.modify') : t('auth.ipWhitelist.add')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const IpWhitelistSettings: React.FC = () => {
  const { t } = useTranslation();

  const MODE_OPTIONS: { value: IpWhitelistMode; label: string; description: string; icon: React.ReactNode }[] = [
    {
      value: 'STRICT',
      label: t('auth.ipWhitelist.modeStrict'),
      description: t('auth.ipWhitelist.modeStrictDescription'),
      icon: <ShieldCheck className="w-4 h-4 text-red-500" />,
    },
    {
      value: 'WARN',
      label: t('auth.ipWhitelist.modeWarning'),
      description: t('auth.ipWhitelist.modeWarningDescription'),
      icon: <ShieldAlert className="w-4 h-4 text-amber-500" />,
    },
    {
      value: 'LOG',
      label: t('auth.ipWhitelist.modeLogOnly'),
      description: t('auth.ipWhitelist.modeLogOnlyDescription'),
      icon: <Info className="w-4 h-4 text-blue-500" />,
    },
  ];

  const [config, setConfig] = useState<IpWhitelistConfig | null>(null);
  const [entries, setEntries] = useState<IpWhitelistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<IpWhitelistEntry | null>(null);
  const [currentIp, setCurrentIp] = useState<string | null>(null);

  // Charger les données initiales
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Valeurs par défaut si API non disponible
      const defaultConfig: IpWhitelistConfig = { enabled: false, mode: 'LOG' };

      // Charger config et entrées en parallèle
      let configData = defaultConfig;
      let entriesData: IpWhitelistEntry[] = [];

      try {
        configData = await ipWhitelistApi.getConfig() || defaultConfig;
      } catch {
        // API non disponible, utiliser les valeurs par défaut
      }

      try {
        entriesData = await ipWhitelistApi.getEntries() || [];
      } catch {
        // API non disponible
      }

      setConfig(configData);
      setEntries(entriesData);

      // Charger l'IP courante séparément (peut échouer sans bloquer)
      try {
        const currentIpData = await ipWhitelistApi.getCurrentIp();
        setCurrentIp(currentIpData?.ip || null);
      } catch {
        // L'IP courante n'est pas critique
        setCurrentIp(null);
      }
    } catch (err: any) {
      logger.error('Erreur chargement whitelist IP', err);
      // Même en cas d'erreur, afficher le composant avec les valeurs par défaut
      setConfig({ enabled: false, mode: 'LOG' });
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async () => {
    if (!config) return;

    try {
      setActionLoading(true);
      await ipWhitelistApi.updateConfig({ enabled: !config.enabled });
      setConfig({ ...config, enabled: !config.enabled });
    } catch (err: any) {
      logger.error('Erreur toggle whitelist', err);
      setError(err.message || t('auth.ipWhitelist.toggleError'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleModeChange = async (mode: IpWhitelistMode) => {
    if (!config) return;

    try {
      setActionLoading(true);
      await ipWhitelistApi.updateConfig({ mode });
      setConfig({ ...config, mode });
    } catch (err: any) {
      logger.error('Erreur changement mode', err);
      setError(err.message || t('auth.ipWhitelist.toggleError'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddEntry = async (data: { ipAddressOrRange: string; label: string; description?: string; expiresAt?: string }) => {
    try {
      setActionLoading(true);
      const newEntry = await ipWhitelistApi.addEntry(data);
      setEntries([newEntry, ...entries]);
      setShowAddModal(false);
    } catch (err: any) {
      logger.error('Erreur ajout entrée', err);
      setError(err.message || t('auth.ipWhitelist.addError'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditEntry = async (data: { ipAddressOrRange: string; label: string; description?: string; expiresAt?: string }) => {
    if (!editingEntry) return;

    try {
      setActionLoading(true);
      const updated = await ipWhitelistApi.updateEntry(editingEntry.id, {
        label: data.label,
        description: data.description,
        expiresAt: data.expiresAt || null,
      });
      setEntries(entries.map(e => e.id === updated.id ? updated : e));
      setEditingEntry(null);
    } catch (err: any) {
      logger.error('Erreur modification entrée', err);
      setError(err.message || t('auth.ipWhitelist.editError'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteEntry = async (id: string) => {
    const confirmed = await confirm({
      title: t('auth.ipWhitelist.confirmDeleteTitle'),
      message: t('auth.ipWhitelist.confirmDeleteText'),
      variant: 'danger',
      confirmText: t('auth.ipWhitelist.delete'),
      cancelText: t('auth.ipWhitelist.cancel'),
    });
    if (!confirmed) return;

    try {
      setActionLoading(true);
      await ipWhitelistApi.deleteEntry(id);
      setEntries(entries.filter(e => e.id !== id));
    } catch (err: any) {
      logger.error('Erreur suppression entrée', err);
      setError(err.message || t('auth.ipWhitelist.deleteError'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      setActionLoading(true);
      const updated = await ipWhitelistApi.updateEntry(id, { isActive });
      setEntries(entries.map(e => e.id === updated.id ? updated : e));
    } catch (err: any) {
      logger.error('Erreur toggle entrée', err);
      setError(err.message || t('auth.ipWhitelist.toggleError'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddCurrentIp = () => {
    if (!currentIp) return;
    setShowAddModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-teal-100 rounded-2xl">
            <Shield className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{t('auth.ipWhitelist.title')}</h2>
            <p className="text-sm text-gray-500">
              {t('auth.ipWhitelist.subtitle')}
            </p>
          </div>
        </div>

        <button
          onClick={() => loadData()}
          disabled={loading || actionLoading}
          className="p-2.5 text-gray-500 hover:bg-gray-100 rounded-xl transition-all duration-200 disabled:opacity-50"
          title={t('auth.ipWhitelist.refresh')}
        >
          <RefreshCw className={`w-5 h-5 ${loading || actionLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Erreur */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-2xl flex items-center gap-3 text-red-700">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Configuration */}
      {config && (
        <div className="bg-white/40 border border-white/50 rounded-2xl p-5 space-y-5">
          {/* Toggle activation */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl ${config.enabled ? 'bg-teal-100' : 'bg-gray-100'}`}>
                {config.enabled ? (
                  <ShieldCheck className="w-5 h-5 text-teal-600" />
                ) : (
                  <ShieldAlert className="w-5 h-5 text-gray-400" />
                )}
              </div>
              <div>
                <h3 className="font-medium text-gray-900">{t('auth.ipWhitelist.enableWhitelist')}</h3>
                <p className="text-sm text-gray-500">
                  {config.enabled
                    ? t('auth.ipWhitelist.whitelistActive')
                    : t('auth.ipWhitelist.allIpsAllowed')
                  }
                </p>
              </div>
            </div>

            <button
              onClick={handleToggleEnabled}
              disabled={actionLoading}
              className={`
                relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200
                ${config.enabled ? 'bg-teal-600' : 'bg-gray-200'}
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
            >
              <span
                className={`
                  inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200
                  ${config.enabled ? 'translate-x-6' : 'translate-x-1'}
                `}
              />
            </button>
          </div>

          {/* Mode de la whitelist */}
          {config.enabled && (
            <div className="pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <Settings className="w-4 h-4 text-gray-500" />
                <h4 className="font-medium text-gray-700">{t('auth.ipWhitelist.controlMode')}</h4>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {MODE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleModeChange(option.value)}
                    disabled={actionLoading}
                    className={`
                      p-3 rounded-xl border text-left transition-all duration-200
                      ${config.mode === option.value
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white/50'
                      }
                      disabled:opacity-50 disabled:cursor-not-allowed
                    `}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {option.icon}
                      <span className="font-medium text-gray-900">{option.label}</span>
                    </div>
                    <p className="text-xs text-gray-500">{option.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* IP actuelle */}
          {currentIp && config.enabled && (
            <div className="pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-700">
                    {t('auth.ipWhitelist.yourCurrentIp')} <span className="font-mono font-medium">{currentIp}</span>
                  </span>
                </div>
                <button
                  onClick={handleAddCurrentIp}
                  disabled={actionLoading || entries.some(e => e.ipAddressOrRange === currentIp)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('auth.ipWhitelist.addCurrentIp')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Liste des entrées */}
      {config?.enabled && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900">
              {t('auth.ipWhitelist.allowedIps')} ({entries.filter(e => e.isActive && !isExpired(e.expiresAt)).length})
            </h3>
            <button
              onClick={() => setShowAddModal(true)}
              disabled={actionLoading}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-xl transition-all duration-200 font-medium text-sm disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              {t('auth.ipWhitelist.add')}
            </button>
          </div>

          {entries.length === 0 ? (
            <div className="text-center py-12 bg-white/40 border border-white/50 rounded-2xl">
              <Globe className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">{t('auth.ipWhitelist.noEntries')}</p>
              <p className="text-sm text-gray-400 mt-1">
                {t('auth.ipWhitelist.noEntriesStrict')}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {entries.map((entry) => (
                <IpEntryCard
                  key={entry.id}
                  entry={entry}
                  onEdit={setEditingEntry}
                  onDelete={handleDeleteEntry}
                  onToggleActive={handleToggleActive}
                  loading={actionLoading}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showAddModal && (
        <AddEditModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddEntry}
          loading={actionLoading}
        />
      )}

      {editingEntry && (
        <AddEditModal
          entry={editingEntry}
          onClose={() => setEditingEntry(null)}
          onSave={handleEditEntry}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default IpWhitelistSettings;
